import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../api";
import { toYouTubeEmbed } from "../youtube";

const EMPTY_LESSON = { title: "", video_url: "", written_content: "", quiz: [] };
const EMPTY_QUESTION = { question: "", choices: ["", ""], correct_index: 0 };

export default function AdminCourseEdit() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [form, setForm] = useState(EMPTY_LESSON);

  const load = () => api.courses.get(id).then(setCourse).catch((e) => setError(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const openNew = () => {
    setEditingLessonId(null);
    setForm(EMPTY_LESSON);
    setModalOpen(true);
  };

  const openEdit = (lesson) => {
    setEditingLessonId(lesson.id);
    setForm({
      title: lesson.title,
      video_url: lesson.video_url || "",
      written_content: lesson.written_content || "",
      quiz: lesson.quiz && lesson.quiz.length > 0 ? lesson.quiz : [],
    });
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order_index: course.lessons.length };
      if (editingLessonId) await api.courses.updateLesson(editingLessonId, payload);
      else await api.courses.addLesson(id, payload);
      setModalOpen(false);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const removeLesson = async (lessonId) => {
    if (!confirm("Delete this lesson?")) return;
    await api.courses.removeLesson(lessonId);
    load();
  };

  const addQuestion = () => {
    if (form.quiz.length >= 5) return;
    setForm({ ...form, quiz: [...form.quiz, { ...EMPTY_QUESTION, choices: ["", ""] }] });
  };
  const removeQuestion = (qi) => setForm({ ...form, quiz: form.quiz.filter((_, i) => i !== qi) });
  const updateQuestion = (qi, field, value) => {
    const quiz = [...form.quiz];
    quiz[qi] = { ...quiz[qi], [field]: value };
    setForm({ ...form, quiz });
  };
  const updateChoice = (qi, ci, value) => {
    const quiz = [...form.quiz];
    const choices = [...quiz[qi].choices];
    choices[ci] = value;
    quiz[qi] = { ...quiz[qi], choices };
    setForm({ ...form, quiz });
  };
  const addChoice = (qi) => {
    const quiz = [...form.quiz];
    if (quiz[qi].choices.length >= 5) return;
    quiz[qi] = { ...quiz[qi], choices: [...quiz[qi].choices, ""] };
    setForm({ ...form, quiz });
  };
  const removeChoice = (qi, ci) => {
    const quiz = [...form.quiz];
    if (quiz[qi].choices.length <= 2) return;
    const choices = quiz[qi].choices.filter((_, i) => i !== ci);
    const correct_index = quiz[qi].correct_index >= choices.length ? 0 : quiz[qi].correct_index;
    quiz[qi] = { ...quiz[qi], choices, correct_index };
    setForm({ ...form, quiz });
  };

  if (error) return <div className="text-red-600">{error}</div>;
  if (!course) return <div className="text-gray-500">Loading…</div>;

  return (
    <div>
      <Link to="/admin" className="text-sm hover:underline" style={{ color: "#154E93" }}>← Back to Courses</Link>
      <div className="flex items-center justify-between mt-2 mb-4">
        <h1 className="text-2xl font-bold" style={{ color: "#154E93" }}>{course.title}</h1>
        <button onClick={openNew} className="px-4 py-2 text-white rounded text-sm font-medium" style={{ backgroundColor: "#154E93" }}>
          + Add Lesson
        </button>
      </div>

      <div className="space-y-3">
        {course.lessons.map((l, i) => (
          <div key={l.id} className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-between">
            <div>
              <div className="font-medium">{i + 1}. {l.title}</div>
              <div className="text-xs text-gray-500 mt-1">
                {l.video_url && "🎬 Video"} {l.written_content && "· 📄 Text"} {l.quiz.length > 0 && `· ❓ ${l.quiz.length} question quiz`}
              </div>
            </div>
            <div className="space-x-3">
              <button onClick={() => openEdit(l)} className="text-blue-600 hover:underline text-sm">Edit</button>
              <button onClick={() => removeLesson(l.id)} className="text-red-600 hover:underline text-sm">Delete</button>
            </div>
          </div>
        ))}
        {course.lessons.length === 0 && (
          <div className="text-gray-400 text-sm bg-white rounded-lg shadow-sm p-6 text-center">No lessons yet — add your first one.</div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={() => setModalOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl my-8">
            <h2 className="text-lg font-bold mb-4" style={{ color: "#154E93" }}>{editingLessonId ? "Edit Lesson" : "Add Lesson"}</h2>

            <label className="block text-xs font-medium text-gray-600 mb-1">Lesson Title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm mb-3" />

            <label className="block text-xs font-medium text-gray-600 mb-1">YouTube Video URL (optional)</label>
            <input
              value={form.video_url}
              onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
              className="w-full border rounded px-2 py-1.5 text-sm mb-1"
            />
            {form.video_url && (
              <div className="mb-3 aspect-video w-full max-w-xs">
                <iframe src={toYouTubeEmbed(form.video_url)} title="preview" className="w-full h-full rounded" allowFullScreen />
              </div>
            )}

            <label className="block text-xs font-medium text-gray-600 mb-1">Written Lesson (optional)</label>
            <textarea value={form.written_content} onChange={(e) => setForm({ ...form, written_content: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm mb-4" rows={4} />

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm">Knowledge Check (0–5 questions)</h3>
                {form.quiz.length < 5 && (
                  <button type="button" onClick={addQuestion} className="text-xs underline" style={{ color: "#154E93" }}>+ Add question</button>
                )}
              </div>

              {form.quiz.map((q, qi) => (
                <div key={qi} className="bg-gray-50 rounded p-3 mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500">Question {qi + 1}</span>
                    <button type="button" onClick={() => removeQuestion(qi)} className="text-xs text-red-600 hover:underline">Remove</button>
                  </div>
                  <input
                    required
                    value={q.question}
                    onChange={(e) => updateQuestion(qi, "question", e.target.value)}
                    placeholder="Question text"
                    className="w-full border rounded px-2 py-1.5 text-sm mb-2"
                  />
                  {q.choices.map((choice, ci) => (
                    <div key={ci} className="flex items-center gap-2 mb-1">
                      <input
                        type="radio"
                        name={`correct-${qi}`}
                        checked={q.correct_index === ci}
                        onChange={() => updateQuestion(qi, "correct_index", ci)}
                        title="Mark as correct answer"
                      />
                      <input
                        required
                        value={choice}
                        onChange={(e) => updateChoice(qi, ci, e.target.value)}
                        placeholder={`Choice ${ci + 1}`}
                        className="flex-1 border rounded px-2 py-1 text-sm"
                      />
                      {q.choices.length > 2 && (
                        <button type="button" onClick={() => removeChoice(qi, ci)} className="text-xs text-red-500">✕</button>
                      )}
                    </div>
                  ))}
                  {q.choices.length < 5 && (
                    <button type="button" onClick={() => addChoice(qi)} className="text-xs underline mt-1" style={{ color: "#154E93" }}>+ Add choice</button>
                  )}
                  <div className="text-xs text-gray-400 mt-1">Select the radio button next to the correct answer</div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm rounded border">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm rounded text-white" style={{ backgroundColor: "#154E93" }}>Save Lesson</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
